import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface Rule {
  system_id: string;
  service_name: string | null;
  window_id: string | null;
  meter_model: string;
  operator_expected_amps_l1: number | null;
  operator_expected_amps_l2: number | null;
  operator_expected_amps_l3: number | null;
  operator_min_keep_amps_l1: number | null;
  operator_min_keep_amps_l2: number | null;
  operator_min_keep_amps_l3: number | null;
  significance_multiplier: number | null;
  z_threshold: number | null;
  ct_ratio: number | null;
  nominal_current_a: number | null;
  meter_swell_threshold_pct: number | null;
  notes: string | null;
  version: number | null;
  updated_at: string | null;
  updated_by: string | null;
  configured: boolean;
}

const FIELD_LABELS: Record<keyof EditableFields, string> = {
  operator_expected_amps_l1: "Exp amps L1",
  operator_expected_amps_l2: "Exp amps L2",
  operator_expected_amps_l3: "Exp amps L3",
  operator_min_keep_amps_l1: "Min keep L1",
  operator_min_keep_amps_l2: "Min keep L2",
  operator_min_keep_amps_l3: "Min keep L3",
  significance_multiplier: "Multiplier",
  z_threshold: "Z threshold",
  ct_ratio: "CT ratio",
  nominal_current_a: "Nominal A",
  meter_swell_threshold_pct: "Swell %",
  notes: "Notes",
};

type EditableFields = {
  operator_expected_amps_l1?: number;
  operator_expected_amps_l2?: number;
  operator_expected_amps_l3?: number;
  operator_min_keep_amps_l1?: number;
  operator_min_keep_amps_l2?: number;
  operator_min_keep_amps_l3?: number;
  significance_multiplier?: number;
  z_threshold?: number;
  ct_ratio?: number;
  nominal_current_a?: number;
  meter_swell_threshold_pct?: number;
  notes?: string;
};

export function AdminRules() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "configured" | "unconfigured">("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Rule | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);

  const reload = () => {
    setLoading(true);
    api
      .get<{ rules: Rule[] }>("/api/acuvim3/rules")
      .then((r) => setRules(r.rules))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const visible = useMemo(() => {
    return rules.filter((r) => {
      if (filter === "configured") return r.configured;
      if (filter === "unconfigured") return !r.configured;
      return true;
    });
  }, [rules, filter]);

  const toggleSelected = (sid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === visible.length) setSelected(new Set());
    else setSelected(new Set(visible.map((r) => r.system_id)));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Acuvim 3 Rules</h1>
          <p className="text-sm text-muted-foreground">
            Per-meter scoring config — published to edges via retained MQTT on save
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="all">All meters</option>
            <option value="configured">Configured only</option>
            <option value="unconfigured">Unconfigured only</option>
          </select>
          <Button
            disabled={selected.size === 0}
            onClick={() => setBatchOpen(true)}
            variant={selected.size > 0 ? "default" : "outline"}
          >
            Batch edit ({selected.size})
          </Button>
        </div>
      </div>

      {error && <div className="text-destructive text-sm">{error}</div>}

      <Card>
        <CardHeader>
          <CardTitle>
            {loading ? "Loading…" : `${visible.length} meter${visible.length === 1 ? "" : "s"}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!loading && visible.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">
              No Acuvim 3 meters match this filter. Ensure memberships exist and meter_model is not 'acuvim_2r'.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <input
                      type="checkbox"
                      checked={selected.size > 0 && selected.size === visible.length}
                      onChange={toggleAll}
                      className="accent-primary"
                    />
                  </TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead className="text-right">Expected (L1/L2/L3)</TableHead>
                  <TableHead className="text-right">Multiplier</TableHead>
                  <TableHead className="text-right">Z-threshold</TableHead>
                  <TableHead className="text-right">Version</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((r) => (
                  <TableRow key={r.system_id} className={cn(!r.configured && "text-muted-foreground")}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selected.has(r.system_id)}
                        onChange={() => toggleSelected(r.system_id)}
                        className="accent-primary"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-foreground">
                        {r.service_name ?? <span className="text-muted-foreground">(unnamed)</span>}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">{r.system_id}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {r.configured
                        ? `${r.operator_expected_amps_l1}/${r.operator_expected_amps_l2}/${r.operator_expected_amps_l3}`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.significance_multiplier != null ? `${r.significance_multiplier.toFixed(1)}×` : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.z_threshold != null ? r.z_threshold.toFixed(1) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {r.version ?? <span className="text-amber-400">not set</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                        {r.configured ? "Edit" : "Configure"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {editing && (
        <EditDialog
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}

      {batchOpen && (
        <BatchEditDialog
          systemIds={Array.from(selected)}
          onClose={() => setBatchOpen(false)}
          onSaved={() => {
            setBatchOpen(false);
            setSelected(new Set());
            reload();
          }}
        />
      )}
    </div>
  );
}

function EditDialog({
  rule,
  onClose,
  onSaved,
}: {
  rule: Rule;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [fields, setFields] = useState<Record<string, string>>(() => {
    const obj: Record<string, string> = {};
    for (const k of Object.keys(FIELD_LABELS) as (keyof EditableFields)[]) {
      const v = rule[k as keyof Rule];
      obj[k] = v == null ? "" : String(v);
    }
    return obj;
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(fields)) {
        if (v === "") continue;
        if (k === "notes") body[k] = v;
        else body[k] = parseFloat(v);
      }
      await api.patch(`/api/acuvim3/rules/${encodeURIComponent(rule.system_id)}`, body);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {rule.service_name ?? rule.system_id}
            {rule.configured && (
              <span className="ml-2 text-xs text-muted-foreground font-normal">
                v{rule.version} · {rule.updated_by ?? "—"}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{rule.system_id}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {(Object.keys(FIELD_LABELS) as (keyof EditableFields)[]).map((k) =>
            k === "notes" ? (
              <div key={k} className="col-span-2 space-y-1">
                <Label htmlFor={k}>{FIELD_LABELS[k]}</Label>
                <Textarea
                  id={k}
                  value={fields[k] ?? ""}
                  onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.value }))}
                  rows={2}
                />
              </div>
            ) : (
              <div key={k} className="space-y-1">
                <Label htmlFor={k}>{FIELD_LABELS[k]}</Label>
                <Input
                  id={k}
                  type="number"
                  step="any"
                  value={fields[k] ?? ""}
                  onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.value }))}
                />
              </div>
            )
          )}
        </div>
        {err && <div className="text-destructive text-sm">{err}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save + publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BatchEditDialog({
  systemIds,
  onClose,
  onSaved,
}: {
  systemIds: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ succeeded: number; failed: number } | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const patch: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(fields)) {
        if (v === "") continue;
        if (k === "notes") patch[k] = v;
        else patch[k] = parseFloat(v);
      }
      if (Object.keys(patch).length === 0) {
        setErr("Nothing to apply — fill at least one field");
        setSaving(false);
        return;
      }
      const body: Record<string, unknown> = { system_ids: systemIds, patch };
      if (systemIds.length > 5) body.confirm = true;
      const r = await api.post<{ succeeded: number; failed: number }>(
        "/api/acuvim3/rules/batch",
        body
      );
      setResult({ succeeded: r.succeeded, failed: r.failed });
      setTimeout(() => onSaved(), 800);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Batch edit — {systemIds.length} meters</DialogTitle>
          <DialogDescription>
            Only fields you fill in will be updated. Unchanged fields keep their current values per meter.
            {systemIds.length > 5 && (
              <span className="block mt-1 text-amber-400">
                &gt;5 meters: confirm flag will be sent automatically.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {(Object.keys(FIELD_LABELS) as (keyof EditableFields)[]).map((k) =>
            k === "notes" ? (
              <div key={k} className="col-span-2 space-y-1">
                <Label htmlFor={`batch-${k}`}>{FIELD_LABELS[k]}</Label>
                <Textarea
                  id={`batch-${k}`}
                  value={fields[k] ?? ""}
                  onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.value }))}
                  rows={2}
                />
              </div>
            ) : (
              <div key={k} className="space-y-1">
                <Label htmlFor={`batch-${k}`}>{FIELD_LABELS[k]}</Label>
                <Input
                  id={`batch-${k}`}
                  type="number"
                  step="any"
                  placeholder="(leave blank to skip)"
                  value={fields[k] ?? ""}
                  onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.value }))}
                />
              </div>
            )
          )}
        </div>
        {err && <div className="text-destructive text-sm">{err}</div>}
        {result && (
          <div className="text-sm">
            <span className="text-emerald-400">{result.succeeded} succeeded</span>
            {result.failed > 0 && (
              <span className="text-destructive"> · {result.failed} failed</span>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Applying…" : `Apply to ${systemIds.length} meters`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
